import { Grid, Skeleton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer } from '@mui/material';
import React from 'react';
import { CerebroSecondaryButton, CerebroText } from '../../../shared/components';
import { EmptyGuid } from '../../../shared/constants';
import { FinancialCovenantType, TermSheetFinancialCovenant, TermSheetRevision } from '../../../shared/models';
import { EnumUtils } from '../../../shared/utils';
import { CovenantDeleteDialog } from './CovenantDeleteDialog';
import { CovenantForm } from './CovenantForm';

interface CovenantsPageProps {
	dealGuid: string | undefined;
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	revision: TermSheetRevision | null;
	isLoading: boolean;
}

export const CovenantsPage: React.FC<CovenantsPageProps> = ({
	dealGuid,
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	revision,
	isLoading,
}) => {
	const [covenantBeingEdited, setCovenantBeingEdited] = React.useState<TermSheetFinancialCovenant | null>(null);
	const [covenantBeingDeleted, setCovenantBeingDeleted] = React.useState<TermSheetFinancialCovenant | null>(null);
	const covenantsProvided: boolean = revision?.covenantsProvided ?? true;

	const onAddCovenant = () => {
		setCovenantBeingEdited({
			id: 0,
			guid: EmptyGuid,
			financialCovenantTypeGuid: '',
			financialCovenantType: {} as FinancialCovenantType,
			covenantName: '',
			covenantTarget: undefined,
			covenantTargetType: 'Equals',
			covenantTargetDisplay: '',
		} as TermSheetFinancialCovenant);
	};

	const closeDrawersAndDialogs = () => {
		setCovenantBeingEdited(null);
		setCovenantBeingDeleted(null);
	};

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<>
			{covenantsProvided && (
				<>
					<CerebroSecondaryButton onClick={() => onAddCovenant()} sx={{ marginBottom: 5 }}>
						Add
					</CerebroSecondaryButton>

					<Grid container spacing={2} alignItems="stretch">
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Name</TableCell>
										<TableCell>Target</TableCell>
										<TableCell>&nbsp;</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{revision?.covenants.map(covenant => {
										return (
											<TableRow key={covenant.guid}>
												<TableCell>{covenant.covenantName}</TableCell>
												<TableCell>
													{covenant.isTargetDefined ? (
														<>
															{EnumUtils.GetCovenantTargetTypeDescription(
																covenant.covenantTargetType
															)}{' '}
															{covenant.covenantTarget}
														</>
													) : (
														<>TBD</>
													)}
												</TableCell>
												<TableCell>
													<CerebroSecondaryButton
														onClick={() => setCovenantBeingEdited(covenant)}
													>
														Edit
													</CerebroSecondaryButton>
													<CerebroSecondaryButton
														onClick={() => setCovenantBeingDeleted(covenant)}
													>
														Delete
													</CerebroSecondaryButton>
												</TableCell>
											</TableRow>
										);
									})}
									{(!revision?.covenants || revision?.covenants.length == 0) && (
										<TableRow>
											<TableCell sx={{ textAlign: 'center' }} colSpan={3}>
												No covenants has been added for the term sheet
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Grid>
				</>
			)}

			{!covenantsProvided && (
				<CerebroText>
					Covenants have been marked as not provided. To update this setting navigate to the general tab.
				</CerebroText>
			)}

			<CovenantForm
				dealGuid={dealGuid}
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				covenant={covenantBeingEdited}
				close={closeDrawersAndDialogs}
			/>

			<CovenantDeleteDialog
				dealGuid={dealGuid}
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				covenant={covenantBeingDeleted}
				close={closeDrawersAndDialogs}
			/>
		</>
	);
};
